import React, { useEffect, useRef, MouseEvent } from 'react';
import './CartesianChart.scss';
import { AnyChart, chartCartesian } from '@d3fc/d3fc-chart';
import { BaseType, select, Selection } from 'd3-selection';
import { ensureXAxisLabelsDoNotOverlap } from 'utils/d3fc/xAxis';
import classNames from 'classnames';
import { autoSizeYAxis } from 'utils/d3fc/yaxis';
import { D3fcElement } from '@d3fc/d3fc-element';
import { useMultipleZoomContext } from 'utils/components/Zoom/ZoomContext';
import { zoomTransform } from 'd3-zoom';
import {
    applyZoomTransformToElement,
    isEqualTransform,
} from 'utils/components/Zoom/chartZoom';
import { Dimensions } from 'utils/types/Dimensions';
import { AnySelection, AnySeries } from '@d3fc/d3fc-series';
import { ScaleLinear } from 'd3-scale';

interface CartesianChartProps<T> {
    autoSizeYAxis?: boolean;
    chartSeries: AnySeries;
    className?: string;
    configureTickIndexesToShow?: (
        selection: AnySelection<SVGElement>,
        tickIndexesToShow: number[]
    ) => void;
    data: T[];
    decorate?: (sel: Selection<BaseType, unknown, null, undefined>) => void;
    enrichChart?: (node: HTMLDivElement, chart: AnyChart) => void;
    height: number;
    id: string;
    width: number;
    yOrient?: 'left' | 'right';
    xOrient?: 'top' | 'bottom' | 'none';
    onClickChart?: (e: MouseEvent<HTMLDivElement>) => void;
    onMouseMove?: (e: MouseEvent<HTMLDivElement>) => void;
    onPlotAreaDimensionsChanged: (nextDimensions: Dimensions) => void;
    suppressZoom?: boolean;
    /*
     * If provided, and there is no ZoomContext, then this xScale will be used
     */
    xScale?: ScaleLinear<number, number>;
    /*
     * If provided, and there is no ZoomContext, then this xScale will be used
     */
    yScale?: ScaleLinear<number, number>;
    xTickFormat?: (d: number) => string;
    yTickFormat?: (d: number) => string;
}
export function CartesianChart<T>(props: CartesianChartProps<T>) {
    const {
        autoSizeYAxis: autoSizeYAxisProps,
        chartSeries,
        className,
        configureTickIndexesToShow,
        xScale: xScaleProps,
        yScale: yScaleProps,
        data,
        decorate,
        enrichChart,
        height,
        id,
        suppressZoom,
        width,
        xTickFormat,
        yTickFormat,
        yOrient = 'left',
        xOrient = 'bottom',
        onClickChart,
        onMouseMove,
        onPlotAreaDimensionsChanged,
    } = props;

    const zoomContext = useMultipleZoomContext();

    const xScale =
        (zoomContext ? zoomContext.zoomedXScale : xScaleProps) ?? xScaleProps;
    const yScale =
        (zoomContext ? zoomContext.zoomedYScale : yScaleProps) ?? yScaleProps;

    const nodeRef = useRef<HTMLDivElement>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const chartRef = useRef<any>();

    useEffect(() => {
        chartRef.current = chartCartesian(xScale, yScale)
            .yOrient(yOrient!)
            .xTickSizeOuter(0)
            .yTickSizeOuter(0)
            .svgPlotArea(chartSeries)
            .decorate((sel: Selection<BaseType, unknown, null, undefined>) => {
                if (decorate) {
                    decorate(sel);
                }
                const plotAreaSelection = sel.select<Element>('.plot-area');

                plotAreaSelection.on('measure.range', (event) => {
                    onPlotAreaDimensionsChanged({
                        width: event.detail.width,
                        height: event.detail.height,
                    });
                });

                if (zoomContext && !suppressZoom) {
                    plotAreaSelection
                        .call(zoomContext.zoomObj)
                        .on('wheel.zoom', null);
                }
            });

        if (xOrient) {
            chartRef.current.xOrient(xOrient);
        }
    }, [
        chartSeries,
        xScale,
        yScale,
        yOrient,
        xOrient,
        onPlotAreaDimensionsChanged,
        suppressZoom,
        zoomContext,
        decorate,
    ]);

    useEffect(() => {
        const node = nodeRef.current!;
        const chart = chartRef.current!;

        if (!node || !chart || !data) {
            return;
        }

        const topLevel = select(node);
        topLevel.datum(data).call(chart);

        if (xTickFormat) {
            chart.xTickFormat(xTickFormat);
        }
        if (yTickFormat) {
            chart.yTickFormat(yTickFormat);
        }

        ensureXAxisLabelsDoNotOverlap(
            chartRef.current,
            undefined,
            configureTickIndexesToShow
        );
        if (autoSizeYAxisProps) {
            autoSizeYAxis(chartRef.current, nodeRef.current!);
        }

        if (enrichChart) {
            enrichChart(nodeRef.current, chartRef.current);
        }

        if (zoomContext) {
            // apply  ZoomTransform to this chart if not synchronized
            const plotAreaSelection = topLevel.select<Element>('.plot-area');
            const plotAreaNode = plotAreaSelection.node();
            if (plotAreaNode) {
                const elementTransform = zoomTransform(plotAreaNode); // obtain ZoomTransform stored on this node

                if (
                    !isEqualTransform(zoomContext.transform, elementTransform)
                ) {
                    applyZoomTransformToElement(
                        plotAreaSelection,
                        zoomContext.zoomObj,
                        zoomContext.transform
                    );
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        configureTickIndexesToShow,
        data,
        xScale,
        yScale,
        zoomContext?.transform,
        zoomContext?.zoomObj,
        chartSeries,
    ]);

    useEffect(() => {
        const plotAreaNode = select(nodeRef.current)
            .select<D3fcElement>('.plot-area')
            .node();

        if (plotAreaNode) {
            // trigger the 'measure.range' event to update the chart plotAreaWidth
            plotAreaNode.requestRedraw();
        }
    }, [height, width]);

    const style = { height: `${height}px`, width: `${width}px` };

    const cssClasses = classNames('CartesianChart', className);

    return (
        <div
            id={id}
            className={cssClasses}
            style={style}
            onMouseMove={onMouseMove}
            onClick={onClickChart}
        >
            <div ref={nodeRef} className="Chart" style={style} />
        </div>
    );
}
